import type { DealPassportType, UploadEntity } from '@dobrozem/sdk';
import { DealService } from '@dobrozem/sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@/components/Button';
import { Loader } from '@/components/Loader';
import { StatusModal } from '@/components/StatusModal';
import { Text } from '@/components/Text';
import { clientSdk, uploadFiles } from '@/lib/sdk';
import type { DealDataTypes } from '@/lib/types';
import { validateFileFormat, validateFileSize } from '@/lib/validation';
import { EditOwnerFullName } from '../EditOwnerFullName';
import { LandDocumentUploader } from '../LandDocumentUploader';
import { PersonalIdentificationDocument } from '../PersonalIdentificationDocument';
import { TaxIdentificationNumberInfo } from '../TaxIdentificationNumberInfo';
import styles from './DocumentsUploaderForm.module.scss';
import { passportFormSchema } from './scheme';
import { useDealMutation } from './useDealMutation';
type Props = {
  advertisementAuthor: {
    firstName: string;
    lastName: string;
    patronymic: string;
  };
  advertisementId: string;
  className?: string;
  dealData?: DealDataTypes;
  dealUserType: 'buyer' | 'seller' | null;
};
type FormValues = {
  documents: (File | string)[];
  ownershipDocument: (File | string)[];
  personalIdentification: {
    newIDCard: string;
    oldPassport: {
      number: string;
      series: string;
    };
  };
  tin: string;
};
const cleanValue = (value: string) => value.replace(/[_\s]/g, '');
export const DocumentsUploaderForm = ({
  advertisementAuthor,
  advertisementId,
  className,
  dealData,
  dealUserType
}: Props) => {
  const [isEditOwnershipDocumentBlockMode, setEditOwnershipDocumentBlockMode] = useState(dealData?.ownershipDocument ? false : true);
  const [isEditPersonalIdentificationBlockMode, setEditPersonalIdentificationBlockMode] = useState(dealData?.passportType ? false : true);
  const [isEditTinBlockMode, setEditTinBlockMode] = useState(dealData?.tin ? false : true);
  const [isLoadingOwnershipDocument, setLoadingOwnershipDocument] = useState(false);
  const [isLoadingSavePassportData, setLoadingSavePassportData] = useState(false);
  const [isLoadingSaveTinData, setLoadingSaveTinData] = useState(false);
  const [isLoadingDocuments, setLoadingDocuments] = useState(false);
  const [isOpenDeleteFileModal, setOpenDeleteFileModal] = useState(false);
  const [isOpenSuccessFileDeletedModal, setOpenSuccessFileDeletedModal] = useState(false);
  const [isDeleteOwnershipDocument, setDeleteOwnershipDocument] = useState(false);
  const [fileIsSuccessUploaded, setFileSuccessUploaded] = useState(false);
  const [isOpenValidationSuccessModal, setOpenValidationSuccessModal] = useState(false);
  const [fileForDelete, setFileForDelete] = useState<null | string>(null);
  const [documentsError, setDocumentsError] = useState('');
  const isIdPassportType = dealData?.passportType === 'ID_CARD';
  const isOldPassportType = dealData?.passportType === 'PASSPORT';
  const ownershipDocument = (dealData?.ownershipDocument as unknown as UploadEntity)?.storagePath;
  const {
    control,
    formState: {
      errors
    },
    handleSubmit,
    setError,
    setValue,
    trigger,
    watch
  } = useForm<FormValues>({
    defaultValues: {
      documents: dealData?.documents ? (dealData?.documents as unknown as UploadEntity[]).map(({
        storagePath
      }) => storagePath) : [],
      ownershipDocument: ownershipDocument ? [ownershipDocument] : [],
      personalIdentification: {
        newIDCard: isIdPassportType ? dealData.passport : '',
        oldPassport: {
          number: isOldPassportType ? dealData.passport?.slice(2) : '',
          series: isOldPassportType ? dealData.passport?.slice(0, 2) : ''
        }
      },
      tin: dealData?.tin ?? ''
    },
    resolver: zodResolver(passportFormSchema)
  });
  const dealMutation = useDealMutation({
    advertisementId,
    dealUserType,
    onSuccess(data) {
      if (data.data.success) {
        setLoadingDocuments(false);
        setError('documents', {
          message: ''
        });
        setDocumentsError('');
        if (!fileForDelete) {
          setFileSuccessUploaded(true);
        }
      }
    }
  });
  const dealMutationSeller = useMutation({
    mutationFn: DealService.updateSellerData<false>,
    onSuccess({
      error,
      response
    }) {
      if (response.status === 200 || response.status === 201) {
        setLoadingOwnershipDocument(false);
        setEditOwnershipDocumentBlockMode(false);
        setError('ownershipDocument', {
          message: ''
        });
        if (!fileForDelete) {
          setFileSuccessUploaded(true);
          setValue('documents', []);
        }
        if ((ownershipDocument || watch('ownershipDocument')[0]) === fileForDelete) {
          setOpenSuccessFileDeletedModal(true);
          setOpenDeleteFileModal(false);
          setValue('ownershipDocument', []);
        }
      }
      setError('ownershipDocument', {
        message: (error as {
          message: string;
        }).message
      });
    }
  });
  const dealValidationMutation = useMutation({
    mutationFn: DealService.updateDealValidationStatus<false>,
    onSuccess({
      error,
      response
    }) {
      if (response.status) {
        setOpenValidationSuccessModal(true);
      }
      console.log(error);
    }
  });
  const handleSavePassportData = async () => {
    const isValid = await trigger('personalIdentification');
    const passportType: DealPassportType = watch('personalIdentification.newIDCard') ? 'ID_CARD' : 'PASSPORT';
    if (isValid) {
      setLoadingSavePassportData(true);
      dealMutation.mutate({
        passport: passportType === 'ID_CARD' ? watch('personalIdentification.newIDCard') : watch('personalIdentification.oldPassport.series') + watch('personalIdentification.oldPassport.number'),
        passportType
      }, {
        onError: error => {
          setError('personalIdentification', {
            message: `Помилка збереження даних:', ${error}`
          });
        },
        onSuccess: () => {
          console.log('Дані успішно збережено');
          setLoadingSavePassportData(false);
          setEditPersonalIdentificationBlockMode(false);
        }
      });
    } else {
      setEditPersonalIdentificationBlockMode(true);
    }
  };
  const handleSaveTinData = async () => {
    const isValid = await trigger('tin');
    if (isValid) {
      setLoadingSaveTinData(true);
      dealMutation.mutate({
        tin: watch('tin')
      }, {
        onError: error => {
          setError('tin', {
            message: `Помилка збереження даних:', ${error}`
          });
        },
        onSuccess: () => {
          console.log('Дані успішно збережено');
          setLoadingSaveTinData(false);
          setEditTinBlockMode(false);
        }
      });
    } else {
      setEditTinBlockMode(true);
    }
  };
  const handleDeleteFile = () => {
    if (isDeleteOwnershipDocument) {
      dealMutationSeller.mutate({
        body: {
          advertisementId,
          ownershipDocument: null
        },
        client: clientSdk
      });
    }
    if (!isDeleteOwnershipDocument && fileForDelete) {
      dealMutation.mutate({
        documentsToRemove: [fileForDelete]
      }, {
        onSuccess: ({
          data
        }) => {
          if (data?.success) {
            setOpenSuccessFileDeletedModal(true);
            setOpenDeleteFileModal(false);
            setValue('documents', watch('documents').filter(file => file !== fileForDelete));
          }
        }
      });
    }
  };
  const onSubmit = (data: FormValues) => {
    console.log(data);
    dealValidationMutation.mutate({
      body: {
        advertisementId
      },
      client: clientSdk
    });
  };
  return <>
      <form className={clsx(styles.wrapper, className)} onSubmit={handleSubmit(onSubmit)}>
        {dealUserType === 'seller' && <EditOwnerFullName advertisementAuthor={advertisementAuthor} />}
        {dealUserType === 'seller' && <Controller control={control} name='ownershipDocument' render={({
        field: {
          name,
          onChange,
          value
        },
        fieldState: {
          error
        }
      }) => {
        const errorMessage = error?.message || Array.isArray(error) && error[0]?.message;
        const handleFileChange = async (files: (File | string)[]) => {
          const file = files[0];
          if (!file || typeof file === 'string') return;

          // Валідація файлу
          const isFileValidFormat = validateFileFormat(file);
          const isFileValidSize = validateFileSize(file);
          if (!isFileValidFormat) {
            setError('ownershipDocument', {
              message: 'Невірний формат файлу.'
            });
            return;
          }
          if (!isFileValidSize) {
            setError('ownershipDocument', {
              message: 'Перевірте розмір файлу, максимально допустимий 25 мб.'
            });
            return;
          }
          setLoadingOwnershipDocument(true);
          try {
            const ownershipDocumentLink = await uploadFiles([file], 'DEAL_OWNERSHIP_DOCUMENT');
            onChange([ownershipDocumentLink[0]]);
            dealMutationSeller.mutate({
              body: {
                advertisementId,
                ownershipDocument: ownershipDocumentLink[0]
              },
              client: clientSdk
            });
          } catch (error) {
            setError('ownershipDocument', {
              message: 'Помилка завантаження файлу'
            });
            console.error('Помилка завантаження файлу:', error);
          } finally {
            setLoadingOwnershipDocument(false);
          }
        };
        return <LandDocumentUploader description='Завантажте фотографії або скан-копії документу, що посвідчує право власності на ділянку: договір купівлі-продажу, дарування, свідоцтво про отримання спадщини.' error={errorMessage} hasUploadedFiles={!!dealData?.ownershipDocument} isEditBlockMode={value.length === 0} isLoading={isLoadingOwnershipDocument} multiple={false} name={name} onChange={handleFileChange} onDelete={file => {
          setDeleteOwnershipDocument(true);
          setFileForDelete(file);
          setOpenDeleteFileModal(true);
        }} setValue={setValue} title='Правовстановлюючий документ на землю' value={value} />;
      }} />}
        <Controller control={control} name='personalIdentification.oldPassport.series' render={({
        field: {
          onChange,
          value
        }
      }) => <>
              <PersonalIdentificationDocument errorId={errors.personalIdentification?.newIDCard} errorNumber={errors.personalIdentification?.oldPassport?.number} errorSeries={errors.personalIdentification?.oldPassport?.series} idCard={watch('personalIdentification.newIDCard')} isEditBlockMode={isEditPersonalIdentificationBlockMode} isLoading={isLoadingSavePassportData} number={watch('personalIdentification.oldPassport.number')} onEdit={() => setEditPersonalIdentificationBlockMode(true)} onIDCardChange={value => setValue('personalIdentification.newIDCard', cleanValue(value))} onNumberChange={value => setValue('personalIdentification.oldPassport.number', cleanValue(value))} onSave={handleSavePassportData} onSeriesChange={value => onChange(cleanValue(value))} series={value} />
              {errors.personalIdentification?.root && <Text color='red' type='p6'>
                  {errors.personalIdentification.root.message}
                </Text>}
              {errors.personalIdentification && <Text color='red' type='p6'>
                  {errors.personalIdentification.message}
                </Text>}
            </>} data-sentry-element="Controller" data-sentry-source-file="DocumentsUploaderForm.tsx" />
        <Controller control={control} name='tin' render={({
        field: {
          onChange,
          value
        }
      }) => <TaxIdentificationNumberInfo error={errors.tin} isEditBlockMode={isEditTinBlockMode} isLoading={isLoadingSaveTinData} onChange={value => onChange(cleanValue(value))} onEdit={() => setEditTinBlockMode(true)} onSave={handleSaveTinData} value={value} />} data-sentry-element="Controller" data-sentry-source-file="DocumentsUploaderForm.tsx" />
        <Controller control={control} name='documents' render={({
        field: {
          name,
          onChange,
          value
        },
        fieldState: {
          error
        }
      }) => {
        const errorMessage = Array.isArray(error) ? error.map((e, index) => e?.message && `Файл ${index + 1}: ${e.message}`).filter(Boolean).join(', ') : error?.message;
        const handleFileChange = async (files: (File | string)[]) => {
          // Фільтруємо файли, щоб залишити тільки File (не рядки)
          const newFiles = files.filter(file => typeof file !== 'string');
          if (newFiles.length === 0) return;

          // Масив для помилок
          const fileErrors: string[] = [];

          // Перевірка кожного файлу на валідність перед завантаженням
          newFiles.forEach(file => {
            // Перевірка формату
            const isFormatValid = validateFileFormat(file);
            if (!isFormatValid) {
              fileErrors.push('Невірний формат файлу');
            }

            // Перевірка розміру
            const isSizeValid = validateFileSize(file);
            if (!isSizeValid) {
              fileErrors.push('Перевірте розмір файлу, максимально допустимий 25 мб.');
            }
          });

          // Якщо є помилки, зупиняємо завантаження
          if (fileErrors.length > 0) {
            setError('documents', {
              message: fileErrors.join(', ')
            });
            setDocumentsError(fileErrors.join(', '));
            return;
          }
          setLoadingDocuments(true);
          try {
            // Завантаження файлів на сервер
            const uploadedFiles = await uploadFiles(newFiles, 'DEAL_DOCUMENT');

            // Додаємо посилання на успішно завантажені файли у value
            const updatedFiles = [...(Array.isArray(value) ? value : []), ...uploadedFiles];
            onChange(updatedFiles);

            // Викликаємо мутацію для збереження посилань на сервері
            dealMutation.mutate({
              documents: uploadedFiles
            });
          } catch (error) {
            console.log(error);
            setError('documents', {
              message: 'Помилка завантаження файлу'
            });
            setDocumentsError('Помилка завантаження файлу');
            console.error('Помилка завантаження файлу:', error);
          } finally {
            setLoadingDocuments(false);
          }
        };
        return <LandDocumentUploader error={documentsError || errorMessage} hasUploadedFiles={false} isEditBlockMode={value.length === 0} isLoading={isLoadingDocuments} multiple={true} name={name} onChange={handleFileChange} onDelete={file => {
          setFileForDelete(file);
          setOpenDeleteFileModal(true);
        }} setValue={setValue} title='Додаткові документи' value={value} />;
      }} data-sentry-element="Controller" data-sentry-source-file="DocumentsUploaderForm.tsx" />

        {!isEditTinBlockMode && !isEditPersonalIdentificationBlockMode && (dealUserType === 'seller' ? !isEditOwnershipDocumentBlockMode : true) && <div className={styles.submitBtnContainer}>
              <Button className={styles.submitBtn} disabled={dealValidationMutation.isPending} green type='submit'>
                Відправити документи на перевірку
              </Button>
              {dealValidationMutation.isPending && <Loader />}
            </div>}
      </form>
      <StatusModal buttonsChildren={<>
            <Button disabled={dealMutation.isPending || dealMutationSeller.isPending} fullWIdth onClick={handleDeleteFile}>
              Так, видалити
            </Button>
            <Button bordered fullWIdth onClick={() => {
        setOpenDeleteFileModal(false);
        setFileForDelete(null);
      }}>
              Скасувати
            </Button>
            {(dealMutation.isPending || dealMutationSeller.isPending) && <Loader center />}
            {dealMutation.error && <Text color='red' type='p6'>
                {dealMutation.error.message}
              </Text>}
          </>} description='Ви дійсно бажаєте видалити цей файл?' logoStatus='sad' onOpenChange={setOpenDeleteFileModal} open={isOpenDeleteFileModal} data-sentry-element="StatusModal" data-sentry-source-file="DocumentsUploaderForm.tsx" />
      <StatusModal buttonsChildren={<Button fullWIdth onClick={() => {
      setOpenSuccessFileDeletedModal(false);
      setFileForDelete(null);
    }}>
            Зрозуміло
          </Button>} description='Файл видалено з платформи' logoStatus='ok' onOpenChange={setOpenSuccessFileDeletedModal} open={isOpenSuccessFileDeletedModal} data-sentry-element="StatusModal" data-sentry-source-file="DocumentsUploaderForm.tsx" />
      <StatusModal buttonsChildren={<Button fullWIdth onClick={() => {
      setFileSuccessUploaded(false);
    }}>
            Дякую
          </Button>} description="Ваші документи успішно завантажено! Якщо ми виявимо якісь проблеми, то обов'язково зв'яжемося з вами" logoStatus='ok' onOpenChange={setFileSuccessUploaded} open={fileIsSuccessUploaded} data-sentry-element="StatusModal" data-sentry-source-file="DocumentsUploaderForm.tsx" />
      <StatusModal buttonsChildren={<Button fullWIdth onClick={() => {
      setOpenValidationSuccessModal(false);
    }}>
            Дякую
          </Button>} description="Ваші документи успішно відправлено на перевірку! Після перевірки ми зв'яжемося з вами" logoStatus='ok' onOpenChange={setOpenValidationSuccessModal} open={isOpenValidationSuccessModal} data-sentry-element="StatusModal" data-sentry-source-file="DocumentsUploaderForm.tsx" />
    </>;
};